import type { Page, BlogPost } from "@/payload-types";
import { siteConfig } from "@/lib/site.config";

/**
 * URL resolution utilities for Payload CMS data
 *
 * These utilities provide a consistent way to resolve URLs from
 * Payload's various link types (page references, custom URLs,
 * phone numbers, email addresses, etc.)
 */

/**
 * Type for polymorphic relationship returned by Payload
 * When relationTo is an array, Payload returns this structure
 * Value can be the full object or just the ID (string)
 */
export type PolymorphicRelationship = {
  relationTo: "pages" | "blog-posts";
  value: string | number | Page | BlogPost;
};

/**
 * Combined type for page parameter - can be direct object or polymorphic
 */
export type PageParam = Page | BlogPost | PolymorphicRelationship | number | string | null;

/**
 * Universal URL resolver for Payload link data
 * Handles all link types: page, custom, phone, email
 * Supports polymorphic relationships (pages, blog-posts)
 *
 * @param type - The type of link (page, custom, phone, email)
 * @param data - Object containing the link data
 * @returns Resolved URL string
 */
export function resolvePayloadUrl(
  type?: "page" | "custom" | "phone" | "email" | "dropdown",
  data?: {
    page?: PageParam;
    url?: string | null;
    phone?: string | null;
    email?: string | null;
  }
): string {
  if (!type || !data) return "#";

  switch (type) {
    case "page": {
      if (data.page && typeof data.page === "object") {
        // Check if it's a polymorphic relationship
        if ("relationTo" in data.page && "value" in data.page) {
          const polymorphic = data.page as PolymorphicRelationship;
          const doc = polymorphic.value;

          // If doc is just an ID (string/number), we can't resolve it without fetching
          if (typeof doc !== "object" || doc === null) return "#";

          // All collections have a slug field
          const slug = "slug" in doc ? doc.slug : null;
          if (!slug) return "#";

          // Build URL based on collection type
          switch (polymorphic.relationTo) {
            case "pages":
              return normalizeSlug(slug);
            case "blog-posts":
              return `${siteConfig.blogPath}/${slug}`;
            default:
              return "#";
          }
        }

        if ("slug" in data.page && "excerpt" in data.page) {
          const blogPost = data.page as BlogPost;
          return `${siteConfig.blogPath}/${blogPost.slug}`;
        }

        // Fallback: Direct page object (legacy single relationTo: "pages")
        if ("slug" in data.page) {
          return normalizeSlug(data.page.slug);
        }
      }
      // If page is just a number (ID), we can't resolve it without fetching
      // In this case, return # - the relationship should be populated
      return "#";
    }

    case "custom": {
      return data.url || "#";
    }

    case "phone": {
      if (data.phone) {
        // Remove spaces and special characters for tel: links
        const cleanPhone = data.phone.replace(/\s/g, "");
        return `tel:${cleanPhone}`;
      }
      return "#";
    }

    case "email": {
      if (data.email) {
        return `mailto:${data.email}`;
      }
      return "#";
    }

    case "dropdown": {
      // Dropdown items don't have their own URL
      return "#";
    }

    default:
      return "#";
  }
}

/**
 * Normalize page slugs for routing
 * Handles null/undefined values and the homepage slug "/"
 *
 * @param slug - The page slug from Payload (can be undefined/null)
 * @returns Normalized URL path
 */
export function normalizeSlug(slug: string | undefined | null): string {
  // Handle null/undefined and the homepage slug
  if (!slug || slug === "/") {
    return "/";
  }

  // Ensure slug starts with /
  if (!slug.startsWith("/")) {
    return `/${slug}`;
  }

  return slug;
}

/**
 * Check if a URL is external
 * Used to determine if we need target="_blank" and rel="noopener"
 * 
 * @param url - The URL to check
 * @returns True if the URL is external
 */
export function isExternalUrl(url: string): boolean {
  if (!url) return false;
  
  // Check for protocol (http://, https://, etc.)
  if (/^https?:\/\//.test(url)) {
    // Check if it's the same domain
    if (typeof window !== "undefined") {
      try {
        const urlObj = new URL(url);
        return urlObj.hostname !== window.location.hostname;
      } catch {
        return true;
      }
    }
    return true;
  }
  
  // mailto: and tel: links are considered "external" for target purposes
  if (url.startsWith("mailto:") || url.startsWith("tel:")) {
    return false; // Don't open these in new tab
  }
  
  return false;
}

/**
 * Get link attributes based on URL type
 * Returns appropriate target and rel attributes
 * 
 * @param url - The URL to check
 * @param newTab - Whether to force opening in new tab
 * @returns Object with target and rel attributes
 */
export function getLinkAttributes(
  url: string,
  newTab?: boolean
): {
  target?: string;
  rel?: string;
} {
  const shouldOpenInNewTab = newTab !== undefined ? newTab : isExternalUrl(url);
  
  if (shouldOpenInNewTab) {
    return {
      target: "_blank",
      rel: "noopener noreferrer",
    };
  }
  
  return {};
}

/**
 * Check if a path is currently active
 * Used for navigation highlighting
 * 
 * @param pathname - Current pathname from Next.js router
 * @param url - URL to check against
 * @returns True if the URL is active
 */
export function isActiveUrl(pathname: string, url: string): boolean {
  // Exact match for home
  if (url === "/") {
    return pathname === "/";
  }
  
  // For other paths, check if current path starts with the URL
  return pathname.startsWith(url);
}
